import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Inject, Injectable, Optional} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {SessionService} from '../utils/session.service';
import {APP_AUTH_OPTIONS, AppAuthOptions, DEFAULT_APP_AUTH_OPTIONS} from './app-auth.options';
import {AppAuthResult} from './app-auth.result';
import {AppAuthJWTToken} from './app-auth.token';
import {getDeepFromObject} from './app-auth.utils';

const TOKEN_STORAGE_KEY = 'auth_app_token';

@Injectable({providedIn: 'root'})
export class AppAuthService {
	private readonly tokenSubject = new BehaviorSubject<AppAuthJWTToken>(this.loadStoredToken());
	private readonly baseUrl = environment.apiUrl + '/auth/';

	constructor(
		private http: HttpClient,
		@Optional() @Inject(APP_AUTH_OPTIONS) private options: AppAuthOptions,
	) {
		this.options = {...DEFAULT_APP_AUTH_OPTIONS, ...this.options};
	}

	authenticate(_strategy: string, data: {email?: string; password?: string; rememberMe?: boolean}): Observable<AppAuthResult> {
		return this.http.post(`${this.baseUrl}login`, data, {observe: 'response'}).pipe(
			map(res => {
				const tokenValue = getDeepFromObject(res.body, 'token', '') as string;
				const token = new AppAuthJWTToken(tokenValue, 'email');
				this.storeToken(token);
				return new AppAuthResult(
					true,
					res,
					SessionService.getWantedUrl(),
					[],
					[],
					token,
				);
			}),
			catchError(err => of(this.failureResult(err))),
		);
	}

	register(_strategy: string, data: unknown): Observable<AppAuthResult> {
		return this.http.post(`${this.baseUrl}register`, data, {observe: 'response'}).pipe(
			map(res => new AppAuthResult(true, res, '/', [], ['Registration successful.'])),
			catchError(err => of(this.failureResult(err))),
		);
	}

	requestPassword(_strategy: string, data: {email?: string}): Observable<AppAuthResult> {
		return this.http.post(`${this.baseUrl}request-pass`, {
			...data,
			app_url: environment.appUrl,
		}, {observe: 'response'}).pipe(
			map(res => new AppAuthResult(
				true,
				res,
				'/',
				[],
				['Les instructions de réinitialisation ont été envoyées par email.'],
			)),
			catchError(err => of(this.failureResult(err))),
		);
	}

	resetPassword(_strategy: string, data: Record<string, unknown> = {}): Observable<AppAuthResult> {
		return this.http.put(`${this.baseUrl}reset-pass`, data, {observe: 'response'}).pipe(
			map(res => new AppAuthResult(
				true,
				res,
				'/auth/login',
				[],
				['Votre mot de passe a été modifié avec succès.'],
			)),
			catchError(err => of(this.failureResult(err))),
		);
	}

	logout(_strategy: string): Observable<AppAuthResult> {
		return this.http.post(`${this.baseUrl}logout`, {}, {observe: 'response'}).pipe(
			tap(() => this.clearToken()),
			map(res => new AppAuthResult(true, res, '/auth/login', [], ['Vous avez été déconnecté avec succès.'])),
			catchError(() => {
				this.clearToken();
				return of(new AppAuthResult(true, null, '/auth/login'));
			}),
		);
	}

	isAuthenticated(): Observable<boolean> {
		return this.tokenSubject.pipe(map(token => token.isValid()));
	}

	onTokenChange(): Observable<AppAuthJWTToken> {
		return this.tokenSubject.asObservable();
	}

	getToken(): Observable<AppAuthJWTToken> {
		return of(this.tokenSubject.value);
	}

	setToken(token: AppAuthJWTToken): Observable<AppAuthJWTToken> {
		this.storeToken(token);
		return of(token);
	}

	clearToken(): void {
		localStorage.removeItem(TOKEN_STORAGE_KEY);
		this.tokenSubject.next(new AppAuthJWTToken());
	}

	private storeToken(token: AppAuthJWTToken): void {
		const raw = JSON.stringify({
			name: token.getName(),
			ownerStrategyName: token.getOwnerStrategyName(),
			createdAt: token.getCreatedAt().getTime(),
			value: token.toString(),
		});
		localStorage.setItem(TOKEN_STORAGE_KEY, raw);
		this.tokenSubject.next(token);
	}

	private loadStoredToken(): AppAuthJWTToken {
		try {
			const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
			if (!raw) {
				return new AppAuthJWTToken();
			}
			const pack = JSON.parse(raw) as {value?: string; ownerStrategyName?: string; createdAt?: number};
			return new AppAuthJWTToken(
				pack.value ?? '',
				pack.ownerStrategyName ?? 'email',
				pack.createdAt ? new Date(pack.createdAt) : undefined,
			);
		} catch {
			return new AppAuthJWTToken();
		}
	}

	private failureResult(err: HttpErrorResponse): AppAuthResult {
		const errors = getDeepFromObject(err.error, 'errors', null)
			?? getDeepFromObject(err.error, 'error', null)
			?? getDeepFromObject(err.error, 'message', null)
			?? err.message
			?? 'Une erreur s\'est produite, veuillez réessayer.';
		const normalized = Array.isArray(errors) ? errors : [String(errors)];
		return new AppAuthResult(false, err, null, normalized);
	}
}

@Injectable({providedIn: 'root'})
export class AppTokenService {
	constructor(private authService: AppAuthService) {}

	get(): Observable<AppAuthJWTToken> {
		return this.authService.getToken();
	}

	set(token: AppAuthJWTToken): Observable<AppAuthJWTToken> {
		return this.authService.setToken(token);
	}

	clear(): Observable<null> {
		this.authService.clearToken();
		return of(null);
	}
}
